/**
 * viewportClipInvalidate — shared clip-handle invalidation for VIEWPORT entities.
 *
 * The single source of truth for "this viewport's persisted clip is no longer
 * valid; drop every artefact that would otherwise re-emit it on export".
 *
 * A non-rectangular VIEWPORT clip is carried by THREE paired DXF artefacts that
 * must be invalidated together (AutoCAD pairs them; leaving any one behind
 * resurrects a stale/dangling clip on the next export):
 *
 *   1. `clippingBoundaryHandle` — the typed entity-body 340 hard-pointer to the
 *      OBJECTS SPATIAL_FILTER boundary (S-VIEWPORT-CLIP-HANDLE, PR #318).
 *   2. group-90 `statusBitFlags` bit 0x10000 — the "non-rectangular clip
 *      boundary" flag. Per the Autodesk DXF reference (AcDbViewport group 90)
 *      and ezdxf `acdb_viewport`, bit 16 (1 << 16 = 65536 = 0x10000) is set iff
 *      the viewport has a non-rectangular clip boundary, so it is PAIRED with
 *      the 340 handle — a flag without a boundary handle is an invalid viewport.
 *   3. a legacy `extraGroups` 340 — documents persisted BEFORE the typed
 *      `clippingBoundaryHandle` field existed carry the clip pointer ONLY as a
 *      verbatim `extraGroups: [{ code: 340, ... }]` passthrough. The DXF writer
 *      intentionally preserves an extraGroups-only 340 when the typed field is
 *      absent (writer.ts buildViewportDropSet — code 340 dropped ONLY when the
 *      typed field emitted), so this legacy carrier must be stripped too or the
 *      cleared clip resurrects on export.
 *   4. the RESOLVED `clipPath` — the WCS clip polygon produced by the import
 *      second-pass resolver (S-VIEWPORT-CLIP-RESOLVE). This is the field the
 *      renderer's stencil pass actually consumes (viewportRenderPass →
 *      beginViewportClipStencil). It is DERIVED from the SPATIAL_FILTER boundary
 *      the 340 handle points at, so when the clip is invalidated the resolved
 *      polygon is equally stale and MUST be dropped — otherwise the renderer
 *      keeps feeding the old polygon into the stencil pass and the viewport stays
 *      visually clipped even though the handle / status bit are gone (Codex #326
 *      P2). `childEntityIds` (the membership index) is NOT cleared here: it is
 *      derived from the viewport's VIEW RECTANGLE, not the clip boundary
 *      (viewportClipFinalize: "Independent of the clip boundary"), so a pure
 *      clip-boundary change (VPCLIP Delete, or a viewport MOVE/COPY that does
 *      not touch the view extents) leaves it valid. The ONE caller that DOES
 *      change the view rectangle — `applyViewportClipSet`, which reshapes
 *      center/width/height + rescales viewHeight — clears `childEntityIds`
 *      ITSELF after calling this helper (Codex #326 P2 round-2), because the
 *      membership becomes stale only when the VIEW EXTENTS move, which is the
 *      command's concern, not this boundary-only helper's.
 *
 * Used by:
 *   - VPCLIP set / remove (viewportCommands.ts) — a clip MUTATION supersedes /
 *     removes the imported boundary, so the imported handle is now stale.
 *   - generic MOVE / COPY / ROTATE / SCALE on a viewport (geometryTransforms.ts)
 *     — the clip boundary is a SEPARATE paperspace entity that these transforms
 *     do NOT move/copy, so a transformed viewport's handle would point at an
 *     un-transformed boundary (wrong clip) and a COPY would duplicate the handle
 *     (two viewports → one SPATIAL_FILTER). Invalidating keeps the typed handle
 *     from becoming WRONG under existing mutations.
 *
 * Slice scope: this is the round-trip slice. RE-AUTHORING a new SPATIAL_FILTER
 * boundary + 340 for the new geometry (so a transformed/reclipped viewport keeps
 * a CORRECT clip rather than losing it) is the deferred slice-3 scope
 * (S-VIEWPORT-CLIP-RESOLVE / VPCLIP authoring); today the correct round-trip
 * behaviour for any clip-affecting mutation is to invalidate, not synthesise.
 */

import type { ViewportEntity } from "@dxf-ai-editor/drawing-model";

/**
 * group-90 (AcDbViewport `statusBitFlags`) bit for a non-rectangular clip
 * boundary: bit 16 = 1 << 16 = 65536 = 0x10000. Paired with the 340 boundary
 * handle (Autodesk DXF reference / ezdxf acdb_viewport). Exported so callers /
 * tests can assert the exact bit.
 */
export const VIEWPORT_NONRECT_CLIP_BIT = 0x10000;

/**
 * The literal `"0"` group-340 value is the documented "no spatial filter"
 * sentinel — a viewport with `clipping_boundary_handle == "0"` is ALREADY
 * unclipped (Autodesk DXF reference AcDbViewport `340 default="0"`; ezdxf
 * `clipping_boundary_handle = DXFAttr(340, default="0", optional=True)`). The
 * parser captures it verbatim (preserve-source) and the writer re-emits it
 * byte-faithfully (`340 0`), so invalidation must NOT delete a literal `"0"`:
 * dropping it would OMIT `340 0` on the next export and break the preserve-
 * source round-trip for an edited-but-unclipped viewport. Only a REAL boundary
 * handle (a hard-pointer ≠ "0") is a clip artefact that must be cleared.
 *
 * Exported so consumers that DETECT a clip (e.g. the delete inverse builder in
 * geometryMutations.ts, which mirrors this same three-carrier check) ignore the
 * sentinel identically — a surviving `340 "0"` after invalidation is NOT a live
 * clip and must not suppress the restore-viewport-clip inverse.
 */
export const NO_CLIP_HANDLE = "0";

/**
 * Return a viewport with EVERY persisted clip artefact invalidated:
 *   - the typed `clippingBoundaryHandle` (340) removed — UNLESS it is the
 *     literal `"0"` no-clip sentinel, which is preserved verbatim (it is not a
 *     boundary reference; deleting it would break the preserve-source round-trip
 *     by omitting `340 0` on export);
 *   - the group-90 non-rectangular-clip bit (0x10000) cleared from
 *     `statusBitFlags` (all OTHER status bits — snap/grid/iso/lock/etc. — kept);
 *   - any legacy `extraGroups` 340 (the pre-typed-field carrier) stripped —
 *     again EXCEPT a literal `"0"` no-clip sentinel carrier, kept verbatim;
 *   - the RESOLVED `clipPath` polygon dropped — it is the field the renderer's
 *     stencil pass consumes and is derived from the now-invalid boundary, so a
 *     leftover polygon would keep the viewport visually clipped (Codex #326 P2).
 *
 * Returns the SAME reference when the viewport carried no clip artefact at all
 * (no REAL handle, no 0x10000 bit, no REAL extraGroups-340, no `clipPath`) so
 * callers can detect a no-op. A viewport that carries ONLY the literal `"0"`
 * sentinel(s) with no other artefact is therefore returned unchanged. Otherwise
 * returns a shallow clone with only the affected fields changed
 * (immutable-document pattern — never mutates the input).
 */
export function invalidateViewportClip(vp: ViewportEntity): ViewportEntity {
  // A literal "0" is the no-clip sentinel, NOT a boundary reference — it must
  // survive invalidation so the preserve-source round-trip keeps `340 0`.
  const hasRealTypedHandle =
    vp.clippingBoundaryHandle !== undefined &&
    vp.clippingBoundaryHandle !== NO_CLIP_HANDLE;
  const hasNonRectBit =
    vp.statusBitFlags !== undefined &&
    (vp.statusBitFlags & VIEWPORT_NONRECT_CLIP_BIT) !== 0;
  const hasRealLegacy340 =
    vp.extraGroups !== undefined &&
    vp.extraGroups.some((g) => g.code === 340 && g.value !== NO_CLIP_HANDLE);
  // The resolved render-time polygon is equally stale once the clip is gone.
  const hasResolvedClipPath = vp.clipPath !== undefined;
  // The retained PAPER-space boundary (native group-340 source) is equally stale:
  // it is the source `recomputeNativeClipPath` re-projects from on a view change.
  // If it survived invalidation, a later pan/zoom (applyViewportSetView /
  // applyViewportSetScale) would RESURRECT the supposedly-removed clip from it
  // (Codex #326 r4 #1). Drop it together with `clipPath`.
  const hasPaperBoundary = vp.clipBoundaryPaperSpace !== undefined;
  // The SOURCE-preserved SPATIAL_FILTER boundary kind is derived from the same
  // now-invalid clip; leaving it would make the inspector report a stale
  // "Polygonal"/"Rectangular" clip mode after the clip is removed (@codex #496
  // P3). Drop it together with `clipPath` / `clipBoundaryPaperSpace`.
  const hasClipBoundaryKind = vp.clipBoundaryKind !== undefined;

  // Nothing clip-related to invalidate — return unchanged so callers can no-op.
  // (A lone literal-"0" sentinel is NOT an artefact, so it falls through here.)
  if (
    !hasRealTypedHandle &&
    !hasNonRectBit &&
    !hasRealLegacy340 &&
    !hasResolvedClipPath &&
    !hasPaperBoundary &&
    !hasClipBoundaryKind
  ) {
    return vp;
  }

  const updated: ViewportEntity = { ...vp };

  // (1) Drop the typed 340 hard-pointer — but keep a literal "0" sentinel.
  if (hasRealTypedHandle) {
    delete updated.clippingBoundaryHandle;
  }

  // (2) Clear ONLY the non-rect-clip bit (0x10000); preserve every other bit.
  if (hasNonRectBit) {
    updated.statusBitFlags = vp.statusBitFlags! & ~VIEWPORT_NONRECT_CLIP_BIT;
  }

  // (3) Strip the legacy extraGroups-340 carrier (pre-typed-field documents) —
  //     but preserve a literal "0" sentinel carrier verbatim.
  if (hasRealLegacy340) {
    updated.extraGroups = vp.extraGroups!.filter(
      (g) => g.code !== 340 || g.value === NO_CLIP_HANDLE,
    );
  }

  // (4) Drop the resolved WCS clip polygon — the renderer's stencil pass reads
  //     this field directly, and it is derived from the SPATIAL_FILTER boundary
  //     the (now-invalidated) 340 handle pointed at. Leaving it would keep the
  //     viewport visually clipped after the user deletes / reshapes the clip.
  //     `childEntityIds` (membership) is left intact — it is independent of the
  //     clip boundary (derived from the view rectangle).
  if (hasResolvedClipPath) {
    delete updated.clipPath;
  }

  // (5) Drop the retained PAPER-space native-340 boundary too (Codex #326 r4 #1).
  //     It is the source `recomputeNativeClipPath` re-projects from on every view
  //     change; leaving it would let the next pan/zoom resurrect the removed clip.
  if (hasPaperBoundary) {
    delete updated.clipBoundaryPaperSpace;
  }

  // (6) Drop the source-preserved SPATIAL_FILTER boundary kind — it describes
  //     the now-invalid clip, so a leftover value would keep the inspector
  //     reporting the removed clip's mode (@codex #496 P3).
  if (hasClipBoundaryKind) {
    delete updated.clipBoundaryKind;
  }

  return updated;
}
